(() => {
  const body = document.body;

  if (body.classList.contains('template-index')) {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    const resetHomeScroll = () => {
      window.requestAnimationFrame(() => window.scrollTo(0, 0));
    };

    window.scrollTo(0, 0);
    window.addEventListener('pageshow', resetHomeScroll);
  }

  const drawer = document.querySelector('[data-mobile-drawer]');
  const openers = document.querySelectorAll('[data-drawer-open]');
  const closers = document.querySelectorAll('[data-drawer-close]');

  const setDrawer = (open) => {
    if (!drawer) return;
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    body.classList.toggle('drawer-open', open);
  };

  openers.forEach((button) => button.addEventListener('click', () => setDrawer(true)));
  closers.forEach((button) => button.addEventListener('click', () => setDrawer(false)));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setDrawer(false);
  });

  document.querySelectorAll('[data-video-toggle]').forEach((button) => {
    const section = button.closest('[data-video-section]');
    const video = section?.querySelector('video');
    if (!video) return;

    button.classList.toggle('is-playing', !video.paused);
    button.addEventListener('click', () => {
      if (video.paused) video.play();
      else video.pause();
    });
    video.addEventListener('play', () => button.classList.add('is-playing'));
    video.addEventListener('pause', () => button.classList.remove('is-playing'));
  });

  document.querySelectorAll('[data-product-form]').forEach((form) => {
    const select = form.querySelector('[data-variant-select]');
    const submit = form.querySelector('[data-submit]');
    const label = form.querySelector('[data-submit-label]');
    if (!select || !submit) return;

    const update = () => {
      const option = select.options[select.selectedIndex];
      const available = option.dataset.available === 'true';
      submit.disabled = !available;
      if (label) label.textContent = available ? 'Dodaj u košaricu' : 'Nije dostupno';
    };

    select.addEventListener('change', update);
    update();
  });

  const initScrollStories = (scope = document) => {
    scope.querySelectorAll('[data-scroll-story]').forEach((story) => {
      if (story.dataset.scrollReady === 'true') return;
      story.dataset.scrollReady = 'true';

      const video = story.querySelector('video');
      const progressBar = story.querySelector('[data-story-progress]');
      const loading = story.querySelector('[data-story-loading]');
      const hint = story.querySelector('[data-story-hint]');
      const skip = story.querySelector('[data-story-skip]');
      const end = story.querySelector('[data-story-end]');
      const captions = [...story.querySelectorAll('[data-story-caption]')];
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!video || reducedMotion) {
        story.classList.add('is-ready');
        return;
      }

      let duration = 0;
      let targetTime = 0;
      let seekFrame = 0;
      let immediateSeekPending = false;
      let lastSet = -1;

      const safeEndTime = () => Math.max(0, duration * 0.94);

      const keepStoryVideoPaused = () => {
        if (!video.paused) video.pause();
      };

      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.pause();
      video.addEventListener('play', keepStoryVideoPaused);

      const captionOpacity = (progress, start, finish) => {
        const span = Math.max(finish - start, 0.01);
        const fade = Math.min(span * 0.25, 0.08);
        if (progress < start || progress > finish) return 0;
        if (progress < start + fade) return (progress - start) / fade;
        if (progress > finish - fade) return (finish - progress) / fade;
        return 1;
      };

      const updateVisuals = (progress) => {
        const safeProgress = Math.min(1, Math.max(0, progress));
        story.style.setProperty('--story-progress', safeProgress);

        if (progressBar) progressBar.style.transform = `scaleX(${safeProgress})`;
        story.classList.toggle('has-progress', safeProgress > 0.025);
        if (hint) hint.setAttribute('aria-hidden', String(safeProgress > 0.025));

        captions.forEach((caption) => {
          const start = Number(caption.dataset.start || 0);
          const finish = Number(caption.dataset.end || 1);
          const opacity = captionOpacity(safeProgress, start, finish);
          caption.style.opacity = opacity.toFixed(3);

          const isMiddle = caption.classList.contains('jojo-scroll-story__caption--middle');
          const y = (1 - opacity) * 22;
          caption.style.transform = isMiddle
            ? `translate(-50%, ${y}px)`
            : `translateY(${y}px)`;
        });
      };

      const calculate = () => {
        const rect = story.getBoundingClientRect();
        const scrollable = Math.max(story.offsetHeight - window.innerHeight, 1);
        const progress = Math.min(1, Math.max(0, -rect.top / scrollable));
        updateVisuals(progress);

        if (duration > 0) targetTime = progress * safeEndTime();
      };

      const seekToTarget = () => {
        if (duration <= 0 || !Number.isFinite(targetTime)) return;
        if (video.seeking) return;

        keepStoryVideoPaused();
        const current = Number.isFinite(video.currentTime) ? video.currentTime : 0;
        const clampedTime = Math.min(safeEndTime(), Math.max(0, targetTime));

        if (
          immediateSeekPending ||
          (Math.abs(clampedTime - lastSet) > 0.035 && Math.abs(clampedTime - current) > 0.025)
        ) {
          immediateSeekPending = false;
          try {
            video.currentTime = clampedTime;
            lastSet = clampedTime;
          } catch (error) {}
        }
      };

      const scheduleSeek = (immediate = false) => {
        immediateSeekPending = immediateSeekPending || immediate;
        if (seekFrame || !story.isConnected) return;

        seekFrame = window.requestAnimationFrame(() => {
          seekFrame = 0;
          seekToTarget();
        });
      };

      const ready = () => {
        duration = Number.isFinite(video.duration) ? video.duration : 0;
        keepStoryVideoPaused();
        story.classList.add('is-ready');
        if (loading) loading.setAttribute('aria-hidden', 'true');
        calculate();
        scheduleSeek(true);
      };

      if (video.readyState >= 1) ready();
      else video.addEventListener('loadedmetadata', ready, { once: true });

      video.addEventListener('canplay', () => {
        keepStoryVideoPaused();
        story.classList.add('is-ready');
      });

      video.addEventListener('seeked', () => {
        keepStoryVideoPaused();
        scheduleSeek();
      });

      video.addEventListener('error', () => {
        story.classList.add('is-ready', 'has-error');
        if (loading) {
          loading.innerHTML = '<p>Video se nije učitao. U uređivaču odaberi video iz Shopify Files.</p>';
        }
      }, { once: true });

      const restoreStory = () => {
        keepStoryVideoPaused();
        window.requestAnimationFrame(() => {
          calculate();
          scheduleSeek(true);
        });
      };

      const updateFromViewport = () => {
        calculate();
        scheduleSeek();
      };

      window.addEventListener('scroll', updateFromViewport, { passive: true });
      window.addEventListener('resize', updateFromViewport);
      window.addEventListener('pageshow', restoreStory);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') restoreStory();
      });

      skip?.addEventListener('click', () => {
        end?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      calculate();
      scheduleSeek(true);
    });
  };

  initScrollStories();
  document.addEventListener('shopify:section:load', (event) => initScrollStories(event.target));
})();
